/**
 * DraftAddTopicDialog
 *
 * Modal for adding topics to a draft checklist. Copy of
 * AddTopicsToChecklistDialog with draft API calls and query invalidation.
 */

import { getTopics, IApiAxiosError, ITopicList } from "@/api";
import {
    addTopicToDraft,
    adminDraftQueryKeys,
} from "@/api/clause-checklists/drafts";
import { TableFilterToolbar } from "@/components/admin/table/TableFilterToolbar";
import { Modal } from "@/components/shared/Modal";
import { Table } from "@/components/shared/table/Table";
import { useFilterModel } from "@/hooks/useFilterModel";
import { handleRowSelection } from "@/utils";
import { Stack, Typography } from "@mui/material";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useRef, useState } from "react";

const columns: GridColDef<ITopicList>[] = [
    {
        field: "name",
        headerName: "Topic Name",
        display: "flex",
        maxWidth: 400,
        flex: 1,
        renderCell: ({ row }) => (
            <Typography variant="body2" noWrap>
                {row.name}
            </Typography>
        ),
    },
    {
        field: "clause_count",
        headerName: "# Clauses",
        type: "number",
        display: "flex",
        flex: 1,
    },
];

interface DraftAddTopicDialogProps {
    open: boolean;
    onClose: () => void;
    draftId: number;
    excludeTopicIds: number[];
}

export const DraftAddTopicDialog = ({
    open,
    onClose,
    draftId,
    excludeTopicIds,
}: DraftAddTopicDialogProps) => {
    const [selectedTopicIds, setSelectedTopicIds] = useState<number[]>([]);
    const [rows, setRows] = useState<ITopicList[]>([]);

    const { filterModel, handleFilterModelChange, resetFilterModel } =
        useFilterModel();

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    const queryClient = useQueryClient();

    const assignedTopicIds = useMemo(
        () => new Set(excludeTopicIds),
        [excludeTopicIds]
    );

    const {
        mutate,
        isPending: mutationPending,
        error: mutationError,
        reset,
    } = useMutation({
        mutationFn: async () => {
            const results = await Promise.allSettled(
                selectedTopicIds.map((topicId) =>
                    addTopicToDraft(draftId, { topic_id: topicId })
                )
            );

            const failures = results.filter((r) => r.status === "rejected");
            if (failures.length > 0) {
                throw new Error(
                    `Failed to add ${failures.length} of ${selectedTopicIds.length} topics`
                );
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: adminDraftQueryKeys.detail(draftId),
            });
            const count = selectedTopicIds.length;
            enqueueSnackbar(
                `${count} topic${count === 1 ? " has" : "s have"} been successfully added to the draft.`,
                { variant: "success" }
            );
            handleClose();
        },
    });

    const { data, isFetching } = useQuery({
        queryKey: [
            "draft-add-topics",
            paginationModel.page,
            paginationModel.pageSize,
            filterModel,
        ],
        queryFn: () =>
            getTopics(
                paginationModel.page + 1,
                paginationModel.pageSize,
                filterModel
            ),
    });

    useEffect(() => {
        if (data) {
            const filteredItems = data.items.filter(
                (topic) => !assignedTopicIds.has(topic.id)
            );
            setRows(filteredItems);
        }
    }, [data, assignedTopicIds]);

    const rowCountRef = useRef(data?.total || 0);

    const rowCount = useMemo(() => {
        if (data?.total !== undefined) {
            const adjustedTotal = Math.max(
                0,
                data.total - assignedTopicIds.size
            );
            rowCountRef.current = adjustedTotal;
        }
        return rowCountRef.current;
    }, [data?.total, assignedTopicIds.size]);

    const handleRowSelectionModelChange = (
        rowSelectionModel: GridRowSelectionModel
    ) => {
        setSelectedTopicIds(handleRowSelection(rowSelectionModel, rows));
    };

    const handleClose = () => {
        onClose();
        setSelectedTopicIds([]);
        reset();
        resetFilterModel();
        setPaginationModel({ page: 0, pageSize: 10 });
    };

    const axiosError = mutationError as IApiAxiosError;

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Add Topics to Draft"
            subtitle="Select topics to add to the draft checklist."
            maxWidth="md"
            isLoading={mutationPending}
            actions={[
                {
                    label: `Add ${selectedTopicIds.length} Topic${selectedTopicIds.length === 1 ? "" : "s"}`,
                    onClick: () => mutate(),
                    variant: "contained",
                    color: "primary",
                    disabled: selectedTopicIds.length === 0 || mutationPending,
                },
            ]}
            footerText={
                axiosError && (
                    <Typography
                        variant="caption"
                        color="error"
                        sx={{ fontSize: "0.75rem" }}
                    >
                        {axiosError.response?.data?.detail ||
                            axiosError.message ||
                            "Failed to add topics to the draft."}
                    </Typography>
                )
            }
        >
            <Stack height={400}>
                <Table
                    loading={isFetching}
                    columns={columns}
                    rows={rows}
                    checkboxSelection
                    onRowSelectionModelChange={handleRowSelectionModelChange}
                    pageSizeOptions={[10, 25, 50]}
                    paginationModel={paginationModel}
                    rowCount={rowCount}
                    onPaginationModelChange={setPaginationModel}
                    paginationMode="server"
                    filterMode="server"
                    sortingMode="server"
                    onFilterModelChange={handleFilterModelChange}
                    onSortModelChange={handleFilterModelChange}
                    showToolbar
                    slots={{
                        toolbar: TableFilterToolbar,
                    }}
                />
            </Stack>
        </Modal>
    );
};
